import { GridColumnRequest, ReportGridRequest } from '@/api/services/common.dto'
import i18n from '@/lang/i18n'
import { StatisticalAnalysisActivityStatisticsDossierDetailViewTableContentCase } from '@/modules/statistical-analysis-module/services/activity-dossier/activity-dossier.dto'
import { activityStatisticsDossierService } from '@/modules/statistical-analysis-module/services/activity-dossier/activity-dossier.service'
import { StatisticalAnalysisViewDetailTableFilters } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-view-detail-table/statistical-analysis-view-detail-table.model'
import { getSelectedRowsIds, parseGroupingParams } from '@/modules/statistical-analysis-module/shared/helpers/helpers'
import { StatiscalAnalysisTypeCases, StatisticalAnalysisDossiersDatasourceResponsesDetailTypes, StatisticalAnalysisDossiersDatasourceResponsesTypes, StatisticalAnalysisDynamicObject, StatisticalAnalysisViewDetailTableGroupingColumns } from '@/modules/statistical-analysis-module/shared/models/shared.model'
import { StatisticalAnalysisGroupingResponseField } from '@/modules/statistical-analysis-module/shared/services/statistical-analysis-grouping/statistical-analysis-grouping.dto'
import { StatisticalAnalysisActivityDossierExternalInfo } from '@/modules/statistical-analysis-module/views/service-activity-dossier/service-activity-dossier.model'
import { EntityIdentifiersRequest } from '@/shared/models/http/http.model'
import { Nullable } from '@/shared/types/app.types'
import { AxiosResponse } from 'axios'
import { chain, extend, reduce } from 'lodash'
import { CommonExportType } from '@/api/resources/common/shared/models/common.models'

export class StatisticalAnalysisHandlerExportTableActivityDossier {
    protected statisticalAnalysisFilters: Nullable<StatisticalAnalysisViewDetailTableFilters>
    protected selectedRows: StatisticalAnalysisDossiersDatasourceResponsesTypes[] | StatisticalAnalysisDossiersDatasourceResponsesDetailTypes[]
    protected columns: GridColumnRequest[]

    constructor(statisticalAnalysisFilters: Nullable<StatisticalAnalysisViewDetailTableFilters>, selectedRows: StatisticalAnalysisDossiersDatasourceResponsesTypes[] | StatisticalAnalysisDossiersDatasourceResponsesDetailTypes[], columns: GridColumnRequest[]) {
        this.statisticalAnalysisFilters = statisticalAnalysisFilters
        this.selectedRows = selectedRows
        this.columns = columns
    }

    public async exportTo(exportType: CommonExportType): Promise<AxiosResponse> {
        const sidebarFilter = this.statisticalAnalysisFilters?.externalFiltering.sidebarFilters as StatisticalAnalysisActivityDossierExternalInfo
        const entityIdentifiersRequest: EntityIdentifiersRequest = {
            entityIdentifiers: []
        }

        const request: ReportGridRequest = {
            sectionLabel: i18n.tc('statisticalAnalysis.dossiers.serviceActivity.activityResults'),
            columns: this.columns,
            entityIdentifiersRequest
        }
        const groupFields = (this.statisticalAnalysisFilters?.grouping as StatisticalAnalysisGroupingResponseField[]).map((item: StatisticalAnalysisGroupingResponseField) => item.fieldName)

        const selectedRows = this.selectedRows as StatisticalAnalysisActivityStatisticsDossierDetailViewTableContentCase[]
        const groupValueFilterRequests: StatisticalAnalysisDynamicObject[] = reduce(selectedRows, (aggr: StatisticalAnalysisDynamicObject[], value: StatisticalAnalysisActivityStatisticsDossierDetailViewTableContentCase) => {
            const valueWithOmittedColumns = chain(value)
                .omitBy((_value: unknown, key: string) =>
                    key.startsWith('reportUCL') || key.startsWith('totalCases') || key.startsWith('totalUCL') ||
                    key.startsWith('totalURV') || key.startsWith('macroUCL')
                )
                .omitBy((value: unknown) => value === null)
                .reduce((aggr: StatisticalAnalysisDynamicObject, value: unknown) => {
                    return parseGroupingParams(aggr, value as StatisticalAnalysisViewDetailTableGroupingColumns)
                }, {})
                .value()
            aggr.push(valueWithOmittedColumns)
            return aggr
        }, [])
        return activityStatisticsDossierService.detailExportTo(
            sidebarFilter,
            request,
            groupFields,
            groupValueFilterRequests,
            exportType
        )
    }

    public async exportExternal(row: StatisticalAnalysisDossiersDatasourceResponsesTypes, exportType: CommonExportType): Promise<AxiosResponse> {
        const sidebarFilter = this.statisticalAnalysisFilters?.externalFiltering.sidebarFilters as StatisticalAnalysisActivityDossierExternalInfo
        const entitySelected = sidebarFilter?.typeSearch?.id as StatiscalAnalysisTypeCases

        const entityIdentifiersRequest: EntityIdentifiersRequest = {
            entityIdentifiers: getSelectedRowsIds(entitySelected, this.selectedRows as StatisticalAnalysisDossiersDatasourceResponsesDetailTypes[])
        }

        const request: ReportGridRequest = {
            sectionLabel: i18n.tc('statisticalAnalysis.dossiers.serviceActivity.activityResults'),
            columns: this.columns,
            entityIdentifiersRequest
        }
        const groupValueFilterRequest: StatisticalAnalysisDynamicObject = chain(row)
            .omitBy((_value: unknown, key: string) =>
                key.startsWith('reportUCL') || key.startsWith('totalCases') || key.startsWith('totalUCL') ||
                key.startsWith('totalURV') || key.startsWith('macroUCL')
            )
            .omitBy((value: unknown) => value === null)
            .reduce((aggr: StatisticalAnalysisDynamicObject, value: unknown) => {
                return parseGroupingParams(aggr, value as StatisticalAnalysisViewDetailTableGroupingColumns)
            }, {})
            .value()
        return activityStatisticsDossierService.detailExternalExportTo(
            sidebarFilter,
            request,
            groupValueFilterRequest,
            exportType
        )
    }
}
